// Colours for the Orbis Lane `lane_function` values (bicycle, drivable, entrance,
// exit, parking, shoulder). The tag is multi-valued (`drivable;entrance`), and
// `drivable` is the spec's default, so it only shows when nothing more specific
// is there: an entrance lane that is also drivable reads as an entrance lane.
//
// Shoulder is grey, not white: the old style drew it white on a white casing,
// which made shoulders invisible.
const LANE_FUNCTION_COLORS = {
  drivable: "#43a047",
  entrance: "#1e88e5",
  exit: "#8e24aa",
  shoulder: "#9e9e9e",
  parking: "#6d4c41",
  bicycle: "#00acc1",
};
// A value outside the spec, so it stays visible instead of passing for drivable.
export const UNKNOWN_LANE_FUNCTION_COLOR = "#d81b60";

/**
 * @param {string|undefined} laneFunction raw tag value, `;`-separated
 * @returns {string[]} one colour per function, most specific first; at least one
 */
export function laneFunctionColors(laneFunction) {
  const values = (laneFunction ?? "")
    .split(";")
    .map((v) => v.trim())
    .filter((v) => v !== "");
  const specific = values.filter((v) => v !== "drivable");
  if (specific.length === 0) {
    return [LANE_FUNCTION_COLORS.drivable];
  }
  return [
    ...new Set(
      specific.map(
        (v) => LANE_FUNCTION_COLORS[v] ?? UNKNOWN_LANE_FUNCTION_COLOR,
      ),
    ),
  ];
}

// An LM lane carries `lane_function` twice: from the LM basemap (29351) and from
// the lane-function layer built on it (38434). The view checks one against the
// other; a value missing on either side is a finding of its own, so it is never
// filled in with the `drivable` default here.
export const BASEMAP_LAYER_ID = "29351";
export const LANE_FUNCTION_LAYER_ID = "38434";

/**
 * lane_function as tagged by one layer, read by its full key so the basemap's
 * value and 38434's can't stand in for each other.
 * @param {{get: (key: string) => any}} feature
 * @param {string} layerId
 */
export function getLaneFunctionOf(feature, layerId) {
  const key = `${layerId}-lane_function`;
  return feature.get(key) ?? feature.get(key + ":step");
}

function functionSet(laneFunction) {
  if (laneFunction === undefined || laneFunction === null) {
    return undefined;
  }
  const values = String(laneFunction)
    .split(";")
    .map((v) => v.trim())
    .filter((v) => v !== "");
  return values.length === 0 ? undefined : [...new Set(values)].sort();
}

/**
 * @typedef {"agree"|"refined"|"disagree"|"basemap-missing"|"layer-missing"|"none"} LaneFunctionStatus
 */

/**
 * Compares the two `lane_function` values, order-insensitively
 * (`entrance;drivable` agrees with `drivable;entrance`).
 * @param {string|undefined} basemapValue `29351-lane_function`
 * @param {string|undefined} layerValue `38434-lane_function`
 * @returns {{status: LaneFunctionStatus, label: string|undefined}}
 *   label is undefined when the two agree or 38434 refines the basemap
 */
export function compareLaneFunction(basemapValue, layerValue) {
  const basemap = functionSet(basemapValue);
  const layer = functionSet(layerValue);
  if (basemap === undefined && layer === undefined) {
    return { status: "none", label: "no lane_function" };
  }
  if (basemap === undefined) {
    return { status: "basemap-missing", label: `no ${BASEMAP_LAYER_ID}` };
  }
  if (layer === undefined) {
    return { status: "layer-missing", label: `no ${LANE_FUNCTION_LAYER_ID}` };
  }
  if (basemap.join(";") === layer.join(";")) {
    return { status: "agree", label: undefined };
  }
  // 38434 keeping every basemap function and adding some (drivable ->
  // drivable;entrance) is a fine-tune of the basemap, not a contradiction.
  if (basemap.every((v) => layer.includes(v))) {
    return { status: "refined", label: undefined };
  }
  return {
    status: "disagree",
    label: `${BASEMAP_LAYER_ID} ≠ ${LANE_FUNCTION_LAYER_ID}`,
  };
}
